import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { ChevronDown, X } from "lucide-react";

function Section({ title, children, defaultOpen = true }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="border-b border-slate-200 py-4">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left"
        data-testid={`filter-section-${title.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">{title}</span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="mt-3 space-y-1.5">{children}</div>}
    </div>
  );
}

function CheckboxList({ options, selected, onChange, max = 12, name }) {
  const [showAll, setShowAll] = React.useState(false);
  const visible = showAll ? options : options.slice(0, max);
  return (
    <div className="space-y-1.5 max-h-72 overflow-auto thin-scroll pr-1">
      {visible.map((opt) => {
        const checked = selected.includes(opt);
        return (
          <label key={opt} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer hover:text-slate-900">
            <Checkbox
              checked={checked}
              onCheckedChange={(v) => onChange(v ? [...selected, opt] : selected.filter((s) => s !== opt))}
              data-testid={`filter-${name}-${opt.replace(/\s+/g, '-').toLowerCase()}`}
            />
            <span className="leading-tight">{opt}</span>
          </label>
        );
      })}
      {options.length > max && (
        <button type="button" onClick={() => setShowAll(!showAll)} className="text-xs font-semibold text-[#002FA7] hover:underline mt-1">
          {showAll ? "Show less" : `+ ${options.length - max} more`}
        </button>
      )}
    </div>
  );
}

export default function FilterSidebar({ taxonomies, filters, onChange, onReset }) {
  const t = taxonomies || {};
  const update = (k, v) => onChange({ ...filters, [k]: v });

  const activeCount = Object.entries(filters).reduce((acc, [k, v]) => {
    if (k === "q" || k === "sort" || k === "page") return acc;
    if (Array.isArray(v)) return acc + v.length;
    if (v === true) return acc + 1;
    if (typeof v === "string" && v) return acc + 1;
    return acc;
  }, 0);

  return (
    <aside className="w-full">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-bold text-slate-900">Filters {activeCount > 0 && <span className="ml-2 text-xs font-mono text-slate-500">({activeCount})</span>}</div>
        {activeCount > 0 && (
          <button type="button" onClick={onReset} data-testid="filter-reset"
            className="text-xs font-semibold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
            <X size={12} /> Reset
          </button>
        )}
      </div>

      <Section title="Verification">
        <label className="flex items-center justify-between text-sm">
          <span className="text-slate-700">Verified providers only</span>
          <Switch checked={!!filters.verified_only} onCheckedChange={(v) => update("verified_only", v)} data-testid="filter-verified-only" />
        </label>
        <label className="flex items-center justify-between text-sm mt-2">
          <span className="text-slate-700">Featured only</span>
          <Switch checked={!!filters.featured_only} onCheckedChange={(v) => update("featured_only", v)} data-testid="filter-featured-only" />
        </label>
      </Section>

      <Section title="Country">
        <Input
          placeholder="e.g. United States"
          value={filters.country || ""}
          onChange={(e) => update("country", e.target.value)}
          data-testid="filter-country"
          className="h-9"
        />
      </Section>

      <Section title="Solution category">
        <CheckboxList options={t.solution_categories || []} selected={filters.category || []} onChange={(v) => update("category", v)} name="cat" />
      </Section>

      <Section title="Provider type" defaultOpen={false}>
        <CheckboxList options={t.provider_types || []} selected={filters.provider_type || []} onChange={(v) => update("provider_type", v)} name="ptype" />
      </Section>

      <Section title="Industry" defaultOpen={false}>
        <CheckboxList options={t.industries || []} selected={filters.industry || []} onChange={(v) => update("industry", v)} name="ind" />
      </Section>

      <Section title="Device / product type" defaultOpen={false}>
        <CheckboxList options={t.device_types || []} selected={filters.device_type || []} onChange={(v) => update("device_type", v)} name="dev" />
      </Section>

      <Section title="Connectivity" defaultOpen={false}>
        <CheckboxList options={t.connectivity || []} selected={filters.connectivity || []} onChange={(v) => update("connectivity", v)} name="conn" />
      </Section>

      <Section title="Sensors" defaultOpen={false}>
        <CheckboxList options={t.sensors || []} selected={filters.sensor || []} onChange={(v) => update("sensor", v)} name="sens" />
      </Section>

      <Section title="Battery / power" defaultOpen={false}>
        <CheckboxList options={t.battery_life || []} selected={filters.battery || []} onChange={(v) => update("battery", v)} name="bat" />
      </Section>

      <Section title="Commercial model" defaultOpen={false}>
        <CheckboxList options={t.commercial_models || []} selected={filters.commercial || []} onChange={(v) => update("commercial", v)} name="com" />
      </Section>

      <Section title="Compliance" defaultOpen={false}>
        <CheckboxList options={t.compliance || []} selected={filters.compliance || []} onChange={(v) => update("compliance", v)} name="comp" />
      </Section>

      <Section title="Use case" defaultOpen={false}>
        <CheckboxList options={t.use_cases || []} selected={filters.use_case || []} onChange={(v) => update("use_case", v)} name="uc" />
      </Section>
    </aside>
  );
}
